import * as THREE from 'three';
import { RENDER } from '../config/tuning';
import { bus } from '../core/EventBus';
import type { PostStack } from './PostStack';

/**
 * The WebGL renderer and the scene it draws.
 *
 * Settings follow SPEC §4.5: no MSAA (FXAA lands in the post stack at M4, and MSAA conflicts
 * with it), and a logarithmic depth buffer, which is what makes a 0.4 m near plane and a 30 km
 * far plane coexist without z-fighting across the Kerani theatre.
 *
 * Camera FOV is specified horizontally (78°) because that is how the reference footage reads;
 * Three.js wants vertical, so the conversion happens here, at the aspect-ratio boundary.
 */

const DEG_TO_RAD = Math.PI / 180;

export class Renderer {
  readonly scene: THREE.Scene;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly canvas: HTMLCanvasElement;

  private renderScale: number = RENDER.dynResMax;
  private cssWidthPx = 1;
  private cssHeightPx = 1;
  private post: PostStack | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      logarithmicDepthBuffer: true,
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setClearColor(0x05080b, 1);
    // Three resets the render stats on every `render()` call, so with a post stack in the path the
    // debug overlay would report the last full-screen quad instead of the frame. Reset once per
    // frame instead, and the counters cover the scene and every pass that follows it.
    this.renderer.info.autoReset = false;

    this.scene = new THREE.Scene();
    this.resize(canvas.clientWidth || 1, canvas.clientHeight || 1);
  }

  /** Backing-buffer size in device pixels, after render scale. */
  get drawingBufferSize(): { widthPx: number; heightPx: number } {
    return {
      widthPx: this.renderer.domElement.width,
      heightPx: this.renderer.domElement.height,
    };
  }

  get info(): THREE.WebGLInfo {
    return this.renderer.info;
  }

  /** Highest anisotropic filtering the device supports — the grid plane at grazing angles wants it. */
  get maxAnisotropy(): number {
    return this.renderer.capabilities.getMaxAnisotropy();
  }

  /** Resize to a CSS pixel size; device pixel ratio and render scale are applied here. */
  resize(cssWidthPx: number, cssHeightPx: number): void {
    this.cssWidthPx = Math.max(1, cssWidthPx);
    this.cssHeightPx = Math.max(1, cssHeightPx);

    const pixelRatio = Math.min(window.devicePixelRatio || 1, RENDER.maxPixelRatio);
    this.renderer.setPixelRatio(pixelRatio * this.renderScale);
    this.renderer.setSize(this.cssWidthPx, this.cssHeightPx, false);

    // Projection belongs to the camera rig (see render/Camera.ts), which owns the FOV, the 4°
    // install tilt, zoom, and the look-around cone. The renderer only sizes the buffer.
    bus.emit('render:resize', {
      widthPx: this.renderer.domElement.width,
      heightPx: this.renderer.domElement.height,
    });
  }

  /**
   * Dynamic resolution scale in [dynResMin, dynResMax] (SPEC §4.5). Wired to a frame-time
   * controller at M10; exposed now so the plumbing exists.
   */
  setRenderScale(scale: number): void {
    const clamped = Math.min(RENDER.dynResMax, Math.max(RENDER.dynResMin, scale));
    if (clamped === this.renderScale) return;
    this.renderScale = clamped;
    this.resize(this.cssWidthPx, this.cssHeightPx);
  }

  get scale(): number {
    return this.renderScale;
  }

  /**
   * Install the video downlink post stack (SPEC §3.3.2). Passing null renders straight to the
   * canvas, which is also what happens when every effect in the stack is switched off — a
   * disabled stack costs nothing rather than costing an identity pass.
   */
  setPostStack(post: PostStack | null): void {
    this.post = post;
    if (post !== null) {
      const { widthPx, heightPx } = this.drawingBufferSize;
      post.resize(widthPx, heightPx);
    }
  }

  /** Draw one frame. All interpolation has already been applied to the scene graph. */
  render(camera: THREE.PerspectiveCamera): void {
    this.renderer.info.reset();
    const post = this.post;
    if (post !== null && post.enabled) {
      post.render(this.renderer, this.scene, camera);
      return;
    }
    this.renderer.setRenderTarget(null);
    this.renderer.render(this.scene, camera);
  }

  /** Match the backing buffer to the element's current CSS size. Cheap; safe to call per frame. */
  syncToCanvasSize(): boolean {
    const widthPx = this.canvas.clientWidth;
    const heightPx = this.canvas.clientHeight;
    if (widthPx === this.cssWidthPx && heightPx === this.cssHeightPx) return false;
    this.resize(widthPx, heightPx);
    return true;
  }

  dispose(): void {
    this.renderer.dispose();
  }
}

/** Convert a horizontal FOV (degrees) to the vertical FOV (degrees) Three.js expects. */
export function horizontalToVerticalFovDeg(fovHDeg: number, aspect: number): number {
  const halfH = fovHDeg * 0.5 * DEG_TO_RAD;
  return (2 * Math.atan(Math.tan(halfH) / aspect)) / DEG_TO_RAD;
}
